import { gql } from '@apollo/client';

import { COMPANY_BASIC, COMPANY_INFO } from './company';
import { COUNTRY } from './country';
import { CRITERIA, CRITERIA_RESULT } from './criteria';
import { DISCUSSION } from './discussion';
import { IDEA, IDEA_BASIC, IDEA_COLLABORATION, IDEA_REPORTS } from './idea';
import { CHANNEL_IDEA_EVALUATION_RESULT } from './idea-channel';
import { INNOVATION } from './innovation';
import { REGION } from './region';
import { TOPIC } from './topic';
import { FILE } from './upload';
import { RANK, USER_BASICS, USER_WITH_RANK } from './user';

export const USER_STATISTICS = gql`
  fragment userStatistics on UserStatistics {
    date
    count
  }
`;

export const USER_ROLE_STATISTICS = gql`
  fragment userRoleStatistics on UserRoleStatistics {
    role
    count
  }
`;

export const USER_COUNTRY_STATISTICS = gql`
  fragment userCountryStatistics on UserCountryStatistics {
    country
    count
  }
`;

export const CHALLENGE_STATISTICS = gql`
  fragment challengeStatistics on ChallengeStatistics {
    type
    date
    count
  }
`;

export const CHALLENGE_APPLICATION_STATISTICS = gql`
  fragment applicationStatistics on ApplicationStatistics {
    type
    date
    count
  }
`;
export const POPULAR_TOPIC = gql`
  fragment popularTopic on PopularTopic {
    topic
    count
  }
`;

export const GET_USER_STATISTICS = gql`
  query UserStatistics($filterBy: UserRole!) {
    userStatistics(filterBy: $filterBy) {
      ...userStatistics
    }
  }
  ${USER_STATISTICS}
`;

export const GET_USER_STATISTICS_WITH_ROLES = gql`
  query UserStatisticsWithRoles($input: UserStatisticsWithRolesInput) {
    userStatisticsWithRoles(input: $input) {
      date
      values {
        role
        count
      }
    }
  }
`;

export const GET_TOTAL_USERS_COUNT = gql`
  query TotalUserCount {
    totalUserCount
  }
`;

export const GET_COUNT_BY_REGION = gql`
  query UserCountByRegion(
    $order: Boolean
    $startDate: String!
    $endDate: String!
  ) {
    userCountByRegion(order: $order, startDate: $startDate, endDate: $endDate) {
      region
      count
    }
  }
`;

export const GET_USER_COMPANY_AFFILIATION = gql`
  query UserCompanyAffiliation {
    userCompanyAffiliation {
      withCompanies
      withoutCompanies
    }
  }
`;

export const GET_USER_COUNT_BY_PURPOSE = gql`
  query UserCountByPurpose {
    userCountByPurpose {
      count
      purpose
    }
  }
`;

export const GET_USER_ROLE_STATISTICS = gql`
  query UserRoleStatistics {
    userRoleStatistics {
      ...userRoleStatistics
    }
  }
  ${USER_ROLE_STATISTICS}
`;

export const GET_USER_COUNTRY_STATISTICS = gql`
  query UserCountryStatistics($filterBy: UserRole!) {
    userCountryStatistics(filterBy: $filterBy) {
      ...userCountryStatistics
    }
  }
  ${USER_COUNTRY_STATISTICS}
`;

export const GET_CHALLENGE_STATISTICS = gql`
  query ChallengeStatistics($filterBy: ChallengeType!) {
    challengeStatistics(filterBy: $filterBy) {
      ...challengeStatistics
    }
  }
  ${CHALLENGE_STATISTICS}
`;

export const GET_CHALLENGE_APPLICATION_STATISTICS = gql`
  query ApplicationStatistics($filterBy: ChallengeType!) {
    applicationStatistics(filterBy: $filterBy) {
      ...applicationStatistics
    }
  }
  ${CHALLENGE_APPLICATION_STATISTICS}
`;

export const GET_POPULAR_TOPICS = gql`
  query popularTopics($limit: Int) {
    popularTopics(limit: $limit) {
      ...popularTopic
    }
  }
  ${POPULAR_TOPIC}
`;

export const GET_TOTAL_CHALLENGES = gql`
  query ChallengeStatisticsByState($input: ChallengeStatisticsInput) {
    challengeStatisticsByState(input: $input) {
      date
      values {
        state
        count
      }
    }
  }
`;

export const GET_TOTAL_CHALLENGES_COUNT = gql`
  query ChallengeStatisticsByState($type: ChallengeType) {
    totalChallengesCount(type: $type)
  }
`;

export const GET_CHALLENGES_BY_TOPICS = gql`
  query ChallengesByTopics($input: ChallengesByTopicsInput) {
    challengesByTopics(input: $input) {
      topic
      count
    }
  }
`;

export const GET_TOTAL_IDEAS_BY_RANGE = gql`
  query DashboardTotalIdeas(
    $startDate: String!
    $endDate: String!
    $statuses: [Status]!
  ) {
    dashboardTotalIdeas(
      startDate: $startDate
      endDate: $endDate
      statuses: $statuses
    ) {
      list {
        name
        count
        date
      }
      total
      __typename
    }
  }
`;

export const GET_IDEA_BUSINESS_AREA = gql`
  query DashboardTopIdeaAreas($statuses: [Status]!) {
    dashboardTopIdeaAreas(statuses: $statuses) {
      name
      count
      __typename
    }
  }
`;

export const GET_IDEA_REGION = gql`
  query DashboardTopIdeaRegions($statuses: [Status]!) {
    dashboardTopIdeaRegions(statuses: $statuses) {
      name
      count
    }
  }
`;

export const GET_IDEA_TOPICS = gql`
  query DashboardTopIdeaTopics($statuses: [Status]!) {
    dashboardTopIdeaTopics(statuses: $statuses) {
      name
      count
    }
  }
`;

export const GET_TOP_IDEAS = gql`
  query DashboardTopIdeas($limit: Int, $statuses: [Status]!) {
    dashboardTopIdeas(limit: $limit, statuses: $statuses) {
      ...idea
    }
  }
  ${IDEA}
  ${INNOVATION}
  ${IDEA_COLLABORATION}
  ${REGION}
  ${TOPIC}
  ${DISCUSSION}
  ${COUNTRY}
  ${IDEA_REPORTS}
  ${FILE}
  ${USER_BASICS}
  ${RANK}
`;

export const GET_SEARCH_CHALLENGE_DATA = gql`
  query DashboardTopIdeas($input: ChallengeInfoDataInput) {
    challengeInfoData(input: $input) {
      companyCount
      profileOwnerCount
      applicationCount
    }
  }
`;

export const GET_USER_COUNTRY_RATIO = gql`
  query UserCountryRatio($startDate: String!, $endDate: String!) {
    userCountryRatio(startDate: $startDate, endDate: $endDate) {
      count
      all
      country
    }
  }
`;

export const GET_USER_LOCATION_ALL = gql`
  query UserCountryRatioAll {
    userCountryRatioAll {
      country
      all
      __typename
    }
  }
`;

export const GET_INSTITUTIONS_BY_TOPICS = gql`
  query CompanyCountByTopic($order: Boolean) {
    companyCountByTopic(order: $order) {
      count
      topic
    }
  }
`;

export const GET_COMPANY_COUNT_BY_LOCATION = gql`
  query companyCountByLocation($startDate: String!, $endDate: String!) {
    companyCountByLocation(startDate: $startDate, endDate: $endDate) {
      count
      location
      all
    }
  }
`;

export const GET_INSTITUTIONS_BY_REGION = gql`
  query CompanyCountByRegion {
    companyCountByRegion {
      count
      region
    }
  }
`;

export const GET_INNOVATION_CONTENT_STATISTICS = gql`
  query InnovationSpaceContentStatistics(
    $startDate: String!
    $endDate: String!
  ) {
    innovationSpaceContentStatistics(startDate: $startDate, endDate: $endDate) {
      name
      type
      ideas {
        count
        status
      }
      challenges
      posts {
        count
        status
      }
      users
    }
  }
`;

export const GET_COMPANY_STATISTICS = gql`
  query CompanyStatisticsGraph($input: CompanyStatisticsInput) {
    companyStatisticsGraph(input: $input) {
      date
      values {
        count
        type
      }
    }
  }
`;

export const GET_TOTAL_COMPANY_COUNT = gql`
  query TotalCompanyCount {
    totalCompanyCount
  }
`;

export const GET_TRENDING_TOPICS = gql`
  query DashboardTrendingTopics {
    dashboardTrendingTopics {
      name
      value
      type
    }
  }
`;

export const GET_TRENDING_REGION = gql`
  query DashboardTrendingRegions {
    dashboardTrendingRegions {
      name
      value
      type
    }
  }
`;

export const GET_USERS_ENGAGEMENT_STATS = gql`
  query DashboardTopIdeaAreas($date: String) {
    getMonthlyUserEngagement(date: $date) {
      date
      posts
      ideas
      challenges
      applicants
      pitches
      __typename
    }
  }
`;

export const GET_USERS_ENGAGEMENT_TOTAL_STATS = gql`
  query GetAllTimeUserInsights {
    getAllTimeUserInsights {
      date
      posts
      ideas
      challenges
      applicants
      pitches
      __typename
    }
  }
`;

export const USERS_GENERIC = gql`
  fragment usersGeneric on DashboardActiveUsersGeneric {
    id
    user {
      ...userWithRank
      companies {
        ...basicInfo
      }
    }
    total
  }
`;

export const POSTS_GENERIC = gql`
  fragment postsGeneric on DashboardPostGeneric {
    value
    previousValue
    total
  }
`;

export const GET_POSTS_ENGAGEMENT_STATS = gql`
  query DashboardInsightsPost($date: String!) {
    dashboardInsightsPost(date: $date) {
      activeCreators {
        ...usersGeneric
      }
      activeAttendees {
        ...usersGeneric
      }
      allTimePosts
      allTimeEvents
      rsvp {
        ...postsGeneric
      }
      publicPosts {
        ...postsGeneric
      }
      internalPosts {
        ...postsGeneric
      }
      __typename
    }
  }
  ${USERS_GENERIC}
  ${POSTS_GENERIC}
  ${USER_WITH_RANK}
  ${COMPANY_BASIC}
  ${COMPANY_INFO}
  ${FILE}
`;

export const GET_IDEA_ENGAGEMENT_STATS = gql`
  query DashboardInsightsIdea($date: String!) {
    dashboardInsightsIdea(date: $date) {
      total
      value
      previousValue
    }
  }
`;

export const GET_INSTITUTIONS_ENGAGEMENT_STATS = gql`
  query GetMonthlyCompanyInsights($date: String) {
    getMonthlyCompanyInsights(date: $date) {
      date
      application
      pitches
    }
  }
`;

export const GET_INNOVATION_ENGAGEMENT_STATS = gql`
  query GetMonthlyInnovationSpaceInsight($date: String) {
    getMonthlyInnovationSpaceInsight(date: $date) {
      date
      posts
      ideas
      members
    }
  }
`;

export const GET_INNOVATION_ENGAGEMENT_TOTAL_STATS = gql`
  query {
    getAllTimeInnovationSpaceInsight {
      date
      posts
      ideas
      members
    }
  }
`;

export const GET_CHALLENGE_APPLICATION_STATS = gql`
  query GetApplicationByRegistration(
    $type: ChallengeType
    $order: Boolean
    $limit: Int
  ) {
    getApplicationByRegistration(type: $type, order: $order, limit: $limit) {
      id
      challenge
      registration
      type
      applications
      profileOwners
      companies
    }
  }
`;

export const GET_CHALLENGE_MANAGER_STATS = gql`
  query GetChallengeManagerStatistics($order: Boolean) {
    getChallengeManagerStatistics(order: $order) {
      user
      upcoming
      draft
      archived
      open
      closed
      sum
    }
  }
`;

export const GET_EVALUATOR_ASSIGNED_STATS = gql`
  query GetChallengeEvaluatorStatistics($order: Boolean) {
    getChallengeEvaluatorStatistics(order: $order) {
      user
      upcoming
      draft
      archived
      open
      closed
      sum
    }
  }
`;

export const GET_INSTITUTIONS_PITCH_UPLOADED_STATS = gql`
  query GetChallengeEvaluatorStatistics($endDate: String) {
    getCompanyPitchStatistics(endDate: $endDate) {
      date
      pitched
      unpitched
    }
  }
`;

export const GET_TOP_IDEAS_BY_HIGHEST_SCORE = gql`
  query GetChallengeEvaluatorStatistics(
    $challengeId: UUID
    $businessArea: [String]
    $limit: Int
  ) {
    dashboardIdeasByAverageScore(
      challengeId: $challengeId
      businessAreas: $businessArea
      limit: $limit
    ) {
      ...ideaBasic
      channelCriteria {
        ...criteria
      }
      channelCriteriaResults {
        ...channelCriteriaResults
      }
    }
  }
  ${IDEA_BASIC}
  ${INNOVATION}
  ${TOPIC}
  ${REGION}
  ${FILE}
  ${USER_BASICS}
  ${RANK}
  ${CRITERIA}
  ${CRITERIA_RESULT}
  ${CHANNEL_IDEA_EVALUATION_RESULT}
`;

export const GET_IDEA_EVALUATIONS_STATS = gql`
  query GetIdeaEvaluationsByStatus($limit: Int) {
    getIdeaEvaluationsByStatus(limit: $limit) {
      user
      total
      done
      pending
      evaluator {
        ...userBasics
      }
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_TOP_CONTRIBUTORS = gql`
  query GetMostActiveUsers($input: MostActiveUsersInput) {
    getMostActiveUsers(input: $input) {
      user {
        ...userWithRank
        companies {
          ...basicInfo
        }
      }
      points
      ideas
      challenges
      upvotes
      contributions
      posts
      applications
    }
  }
  ${USER_WITH_RANK}
  ${COMPANY_BASIC}
  ${COMPANY_INFO}
  ${FILE}
`;

export const GET_SPACE_IDEAS_BY_AVERAGE_SCORE = gql`
  query DashboardSpaceIdeasByAverageScore(
    $spaceId: UUID!
    $challengeId: UUID
    $businessAreas: [String]
    $limit: Int
  ) {
    dashboardSpaceIdeasByAverageScore(
      spaceId: $spaceId
      challengeId: $challengeId
      businessAreas: $businessAreas
      limit: $limit
    ) {
      ...ideaBasic
    }
  }
  ${IDEA_BASIC}
  ${INNOVATION}
  ${TOPIC}
  ${REGION}
  ${FILE}
  ${USER_BASICS}
  ${RANK}
  ${CRITERIA}
  ${CRITERIA_RESULT}
`;

export const GET_SPACE_IDEA_EVALUATIONS_BY_STATUS = gql`
  query DashboardSpaceIdeaEvaluationsByStatus($spaceId: UUID!, $limit: Int) {
    dashboardSpaceIdeaEvaluationsByStatus(spaceId: $spaceId, limit: $limit) {
      user
      total
      done
      pending
      evaluator {
        ...userBasics
      }
    }
  }
  ${FILE}
  ${USER_BASICS}
`;

export const GET_SPACE_IDEAS_COUNT = gql`
  query GetInnovationSpaceIdeasCount(
    $spaceId: UUID!
    $startDate: String!
    $endDate: String!
  ) {
    getInnovationSpaceIdeasCount(
      spaceId: $spaceId
      startDate: $startDate
      endDate: $endDate
    ) {
      title
      state
      ideas
    }
  }
`;

export const GET_SPACE_STAGES = gql`
  query GetIdeaSpaceStages($spaceId: UUID!, $challengeId: UUID!) {
    getIdeaSpaceStages(spaceId: $spaceId, challengeId: $challengeId) {
      stage
      ideas
    }
  }
`;

export const GET_COUNT_IDEAS_BY_AREAS = gql`
  query CountIdeasByAreas(
    $spaceId: UUID!
    $startDate: String!
    $endDate: String!
    $order: Boolean
  ) {
    countIdeasByAreas(
      spaceId: $spaceId
      startDate: $startDate
      endDate: $endDate
      order: $order
    ) {
      areas
      ideas
    }
  }
`;

export const GET_COUNT_IDEAS_BY_REGION = gql`
  query CountIdeasByRegionAndSpace(
    $spaceId: UUID!
    $startDate: String!
    $endDate: String!
    $order: Boolean
  ) {
    countIdeasByRegionAndSpace(
      spaceId: $spaceId
      startDate: $startDate
      endDate: $endDate
      order: $order
    ) {
      region
      ideas
    }
  }
`;

export const GET_COUNT_IDEAS_BY_COUNTRY = gql`
  query CountIdeasByCountry(
    $spaceId: UUID!
    $startDate: String!
    $endDate: String!
    $order: Boolean
  ) {
    countIdeasByCountry(
      spaceId: $spaceId
      startDate: $startDate
      endDate: $endDate
      order: $order
    ) {
      country
      ideas
    }
  }
`;

export const GET_COUNT_IDEAS_BY_TOPIC = gql`
  query CountIdeasByTopicAndSpace(
    $spaceId: UUID!
    $startDate: String!
    $endDate: String!
    $order: Boolean
  ) {
    countIdeasByTopicAndSpace(
      spaceId: $spaceId
      startDate: $startDate
      endDate: $endDate
      order: $order
    ) {
      topic
      ideas
    }
  }
`;

export const GET_COUNT_EVENT_ATTENDEE = gql`
  query CountSpaceEventAttendents(
    $spaceId: UUID!
    $startDate: String!
    $endDate: String!
    $order: Boolean
  ) {
    countSpaceEventAttendents(
      spaceId: $spaceId
      startDate: $startDate
      endDate: $endDate
      order: $order
    ) {
      event
      rsvp
    }
  }
`;

export const GET_INNOVATION_OVERVIEW_STATS = gql`
  query InnovationSpaceOverview($spaceId: UUID!) {
    innovationSpaceOverview(spaceId: $spaceId) {
      challenges
      ideas {
        with
        count
      }
      colleagues
      posts
      events
    }
  }
`;

export const GET_TOTAL_POSTS_COUNT = gql`
  query DashboardPostCount(
    $event: Boolean = false
    $startDate: String!
    $endDate: String!
  ) {
    dashboardPostCount(
      event: $event
      startDate: $startDate
      endDate: $endDate
    ) {
      name
      total
    }
  }
`;

export const GET_TOP_EVENTS = gql`
  query dashboardTopEvents($limit: Int = 10) {
    dashboardTopEvents(limit: $limit) {
      name
      total
    }
  }
`;
